import { ProjectTab } from '@/components/projects/ProjectPicker';

/**
 * Get the route for a project with optional tab
 */
export function getProjectRoute(projectId: string, tab?: ProjectTab): string {
  const basePath = `/project/${projectId}`;
  return tab ? `${basePath}?tab=${tab}` : basePath;
}

/**
 * Store the last used project ID in localStorage
 */
export function setLastUsedProject(projectId: string): void {
  try {
    localStorage.setItem('corbel_last_project', projectId);
  } catch (error) {
    console.error('Failed to store last used project:', error);
  }
}

/**
 * Get the last used project ID from localStorage
 */
export function getLastUsedProject(): string | null {
  try {
    return localStorage.getItem('corbel_last_project');
  } catch (error) {
    console.error('Failed to retrieve last used project:', error);
    return null;
  }
}

/**
 * Clear the last used project from localStorage
 */
export function clearLastUsedProject(): void {
  try {
    localStorage.removeItem('corbel_last_project');
  } catch (error) {
    console.error('Failed to clear last used project:', error);
  }
}

/**
 * Determine navigation strategy based on project count and action
 */
export interface QuickActionNavigation {
  type: 'direct' | 'picker' | 'create';
  projectId?: string;
  tab?: ProjectTab;
}

export function getQuickActionNavigation(
  projects: Array<{ id: string }>,
  targetTab: ProjectTab,
  preferLastUsed: boolean = true
): QuickActionNavigation {
  // No projects: create new
  if (projects.length === 0) {
    return { type: 'create', tab: targetTab };
  }

  // Single project: navigate directly
  if (projects.length === 1) {
    const projectId = projects[0].id;
    setLastUsedProject(projectId);
    return { type: 'direct', projectId, tab: targetTab };
  }

  // Multiple projects: check if we should use last used
  if (preferLastUsed) {
    const lastUsedId = getLastUsedProject();
    const lastUsedProject = projects.find((p) => p.id === lastUsedId);

    // If last used project exists and is still accessible, use it
    if (lastUsedProject) {
      return { type: 'direct', projectId: lastUsedId!, tab: targetTab };
    }
  }

  // Multiple projects, no preference: show picker
  return { type: 'picker', tab: targetTab };
}

/**
 * Quick action metadata for each action type
 */
export const QUICK_ACTION_CONFIG = {
  'upload-cim': {
    title: 'Upload & Analyze CIM',
    description: 'Select a project to upload and analyze a Confidential Information Memorandum',
    tab: 'cim' as ProjectTab,
    requiresProject: true,
  },
  'create-ic-memo': {
    title: 'Create IC Memo',
    description: 'Select a project to create an Investment Committee memorandum',
    tab: 'ic-memo' as ProjectTab,
    requiresProject: true,
  },
  'build-model': {
    title: 'Build Financial Model',
    description: 'Select a project to build or analyze a financial model',
    tab: 'financial-model' as ProjectTab,
    requiresProject: true,
  },
  'legal-documents': {
    title: 'Legal Documents',
    description: 'Generate and manage legal documents using AI-powered templates',
    tab: 'legal' as ProjectTab,
    requiresProject: true,
  },
  'ask-ai': {
    title: 'Ask AI Assistant',
    description: 'Start a conversation with the AI assistant about a project',
    tab: 'chat' as ProjectTab,
    requiresProject: false, // Can work without project context
  },
  'ic-committee': {
    title: 'IC Committee',
    description: '8 AI experts analyze 40+ dimensions for institutional-grade investment insights',
    tab: 'ic-committee' as ProjectTab,
    requiresProject: true,
  },
  'transaction-search': {
    title: 'Transaction Search',
    description: 'Search precedent M&A and financing transactions',
    tab: 'transaction-search' as ProjectTab,
    requiresProject: true,
  },
  'presentations': {
    title: 'Create Presentation',
    description: 'Generate AI-powered presentations with Gamma.app',
    tab: 'presentations' as ProjectTab,
    requiresProject: true,
  },
} as const;

export type QuickActionType = keyof typeof QUICK_ACTION_CONFIG;
